import { apiClient } from "./client";

export const profileApi = {
  me: () => apiClient.get("/profile").then((r) => r.data),
  // No submitKyc: verification belongs to WSO2. kycStatus and kyc_url on the
  // profile are mirrored from the IdP, and the user completes KYC in the
  // onboarding portal, ARMF never marks its own customer verified.
};

// Transfer verification preference: OTP (email), PIN (6-digit), or BOTH.
// Setting/changing/removing the PIN and switching mode all require a fresh
// PIN_SETUP OTP as proof, see requestOtp() below.
export const securityApi = {
  get: () => apiClient.get("/settings/security").then((r) => r.data),
  requestOtp: () => apiClient.post("/settings/security/otp").then((r) => r.data),
  setPin: (otpCode, pin) =>
    apiClient.post("/settings/security/pin", { otpCode, pin }).then((r) => r.data),
  removePin: (otpCode) =>
    apiClient.delete("/settings/security/pin", { data: { otpCode } }).then((r) => r.data),
  setMode: (otpCode, mode) =>
    apiClient.post("/settings/security/mode", { otpCode, mode }).then((r) => r.data),
};

export const accountsApi = {
  list: () => apiClient.get("/accounts").then((r) => r.data),
  create: (payload) => apiClient.post("/accounts", payload).then((r) => r.data),
  findById: (id) => apiClient.get(`/accounts/${id}`).then((r) => r.data),
  depositInstructions: (id) =>
    apiClient.get(`/accounts/${id}/deposit-instructions`).then((r) => r.data),
  topup: (id, payload) =>
    apiClient
      .post(
        `/accounts/${id}/topup`,
        typeof payload === "object" ? payload : { amount: payload }
      )
      .then((r) => r.data),
  // No updateStatus here on purpose: wallet status is not customer-editable.
  // It is set by ARM Financial (and, once the IdP exposes it, mirrored from
  // WSO2), see accountService.applyIdpStatus on the API side.
};

export const fundingBankAccountsApi = {
  list: () => apiClient.get("/funding-bank-accounts").then((r) => r.data),
  create: (payload) =>
    apiClient.post("/funding-bank-accounts", payload).then((r) => r.data),
  remove: (id) =>
    apiClient.delete(`/funding-bank-accounts/${id}`).then((r) => r.data),
};

export const beneficiariesApi = {
  list: (params = {}) =>
    apiClient.get("/beneficiaries", { params }).then((r) => r.data),
  add: (payload) =>
    apiClient.post("/beneficiaries", payload).then((r) => r.data),
  findById: (id) => apiClient.get(`/beneficiaries/${id}`).then((r) => r.data),
  update: (id, payload) =>
    apiClient.patch(`/beneficiaries/${id}`, payload).then((r) => r.data),
  remove: (id) =>
    apiClient.delete(`/beneficiaries/${id}`).then((r) => r.data),
};

export const transfersApi = {
  quote: (payload) =>
    apiClient.post("/transfers/quote", payload).then((r) => r.data),
  // Emails (and SMSes, best-effort) a 6-digit code required by create().
  requestOtp: () => apiClient.post("/transfers/otp").then((r) => r.data),
  // Pass a stable idempotencyKey per transfer attempt so a double-click or
  // network retry does not create two transfers (backend dedupes on it).
  create: (payload, idempotencyKey) =>
    apiClient
      .post(
        "/transfers",
        payload,
        idempotencyKey
          ? { headers: { "Idempotency-Key": idempotencyKey } }
          : undefined
      )
      .then((r) => r.data),
  list: () => apiClient.get("/transfers").then((r) => r.data),
  findById: (id) => apiClient.get(`/transfers/${id}`).then((r) => r.data),
  // The customer-visible half of the AML limits: what they may send, and how
  // much of today's and this month's allowance is left. Safe to show, these
  // are the limits a customer is expected to know. The hidden risk layer is
  // never exposed here.
  limits: () => apiClient.get("/transfers/limits").then((r) => r.data),
  cancel: (id) =>
    apiClient.post(`/transfers/${id}/cancel`).then((r) => r.data),
};

// Which partner can actually pay out in a given currency. The recipient form
// used to hardcode "SWIFT" and "Fedwire", neither is a partner ARMF has.
export const payoutProvidersApi = {
  forCurrency: (currency) =>
    apiClient
      .get("/payout-providers", { params: currency ? { currency } : {} })
      .then((r) => r.data),
};

export const conversionsApi = {
  rate: (from, to) =>
    apiClient.get(`/fx-rates/${from}/${to}`).then((r) => r.data),
  quote: (payload) =>
    apiClient.post("/conversions/quote", payload).then((r) => r.data),
  create: (payload) =>
    apiClient.post("/conversions", payload).then((r) => r.data),
  list: () => apiClient.get("/conversions").then((r) => r.data),
};

export const transactionsApi = {
  list: () => apiClient.get("/transactions").then((r) => r.data),
};

export const scheduledTransfersApi = {
  list: () => apiClient.get("/scheduled-transfers").then((r) => r.data),
  create: (payload) => apiClient.post("/scheduled-transfers", payload).then((r) => r.data),
  cancel: (id) => apiClient.delete(`/scheduled-transfers/${id}`).then((r) => r.data),
};

export const moneyRequestsApi = {
  list: () => apiClient.get("/money-requests").then((r) => r.data),
  create: (payload) => apiClient.post("/money-requests", payload).then((r) => r.data),
  getById: (id) => apiClient.get(`/money-requests/${id}`).then((r) => r.data),
  updateStatus: (id, status) => apiClient.patch(`/money-requests/${id}`, { status }).then((r) => r.data),
};

export const invoicesApi = {
  list: () => apiClient.get("/invoices").then((r) => r.data),
  create: (payload) => apiClient.post("/invoices", payload).then((r) => r.data),
  updateStatus: (id, status) => apiClient.patch(`/invoices/${id}`, { status }).then((r) => r.data),
  pdf: (id) => apiClient.get(`/invoices/${id}/pdf`, { responseType: "blob" }).then((r) => r.data),
  send: (id) => apiClient.post(`/invoices/${id}/send`).then((r) => r.data),
};

export const bsbApi = {
  // Pre-flight BSB validation against the AusPayNet directory.
  lookup: (code) => apiClient.get(`/bsb/${code}`).then((r) => r.data),
};

export const bankAccountValidationApi = {
  validateAustralia: (payload) =>
    apiClient.post("/bank-accounts/validate-au", payload).then((r) => r.data),
};

export const billsApi = {
  listBillers: () => apiClient.get("/billers").then((r) => r.data),
  listBills: () => apiClient.get("/bills").then((r) => r.data),
  pay: (id) => apiClient.post(`/bills/${id}/pay`).then((r) => r.data),
};

export const cardsApi = {
  list: () => apiClient.get("/cards").then((r) => r.data),
  create: (payload) => apiClient.post("/cards", payload).then((r) => r.data),
  update: (id, payload) => apiClient.patch(`/cards/${id}`, payload).then((r) => r.data),
};

export const usersApi = {
  search: (q) => apiClient.get("/users/search", { params: { q } }).then((r) => r.data),
};

export const creditReportApi = {
  get: () => apiClient.get("/credit-report").then((r) => r.data),
  upsert: (payload) => apiClient.put("/credit-report", payload).then((r) => r.data),
};

export const referralApi = {
  summary: () => apiClient.get("/referrals/summary").then((r) => r.data),
  listInvites: () => apiClient.get("/referrals/invites").then((r) => r.data),
  createInvite: (payload) => apiClient.post("/referrals/invites", payload).then((r) => r.data),
  updateInvite: (id, status) =>
    apiClient.patch(`/referrals/invites/${id}`, { status }).then((r) => r.data),
};

export const budgetsApi = {
  list: () => apiClient.get("/budgets").then((r) => r.data),
  set: (payload) => apiClient.post("/budgets", payload).then((r) => r.data),
  update: (id, payload) => apiClient.patch(`/budgets/${id}`, payload).then((r) => r.data),
  remove: (id) => apiClient.delete(`/budgets/${id}`).then((r) => r.data),
};

export const retirementPlansApi = {
  list: () => apiClient.get("/retirement-plans").then((r) => r.data),
  create: (payload) => apiClient.post("/retirement-plans", payload).then((r) => r.data),
  update: (id, payload) => apiClient.patch(`/retirement-plans/${id}`, payload).then((r) => r.data),
  remove: (id) => apiClient.delete(`/retirement-plans/${id}`).then((r) => r.data),
  projection: (currentAge) =>
    apiClient
      .get("/retirement-projection", { params: currentAge != null ? { currentAge } : {} })
      .then((r) => r.data),
};

export const socialPaymentsApi = {
  listGroups: () => apiClient.get("/groups").then((r) => r.data),
  createGroup: (payload) => apiClient.post("/groups", payload).then((r) => r.data),
  addExpense: (payload) => apiClient.post("/expenses", payload).then((r) => r.data),
  listExpenses: (groupId) => apiClient.get(`/groups/${groupId}/expenses`).then((r) => r.data),
  getBalances: (groupId) => apiClient.get(`/groups/${groupId}/balances`).then((r) => r.data),
  markSplitPaid: (splitId) =>
    apiClient.patch(`/expense-splits/${splitId}/paid`).then((r) => r.data),
  sendReminder: (splitId) =>
    apiClient.post(`/expense-splits/${splitId}/reminder`).then((r) => r.data),
};

export const qrPaymentsApi = {
  parse: (payload) => apiClient.post("/qr-payments/parse", payload).then((r) => r.data),
  create: (payload, idempotencyKey) =>
    apiClient
      .post(
        "/qr-payments",
        payload,
        idempotencyKey
          ? { headers: { "Idempotency-Key": idempotencyKey } }
          : undefined
      )
      .then((r) => r.data),
};

export const investmentsApi = {
  list: () => apiClient.get("/investments").then((r) => r.data),
  create: (payload) => apiClient.post("/investments", payload).then((r) => r.data),
  update: (id, payload) => apiClient.patch(`/investments/${id}`, payload).then((r) => r.data),
  remove: (id) => apiClient.delete(`/investments/${id}`).then((r) => r.data),
};

export const goalsApi = {
  list: () => apiClient.get("/goals").then((r) => r.data),
  create: (payload) => apiClient.post("/goals", payload).then((r) => r.data),
  update: (id, payload) => apiClient.patch(`/goals/${id}`, payload).then((r) => r.data),
  remove: (id) => apiClient.delete(`/goals/${id}`).then((r) => r.data),
};
